import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Accedi() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const sendForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const { data } = await api.post("/auth/forgot", { email: forgotEmail });
      toast.success(data.message);
      setForgotOpen(false); setForgotEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setForgotLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") { const u = await login(form.email, form.password); toast.success("Bentornato."); nav(u.role === "admin" ? "/admin" : "/area-studente"); return; }
      else { await register(form.name, form.email, form.password); toast.success("Registrazione completata."); }
      nav("/area-studente");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const field = "w-full bg-transparent border-b border-[#2A1F14]/15 py-3 text-[#2A1F14] focus:border-gold outline-none transition-colors placeholder:text-[#2A1F14]/30";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 constellation">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-12">
          <span className="font-display text-2xl tracking-[0.2em] text-[#2A1F14]">LUX</span>
          <span className="w-1 h-1 rounded-full bg-gold" />
          <span className="font-display text-2xl tracking-[0.2em] text-gold">AETERNA</span>
        </Link>

        <div className="glass rounded-2xl p-8">
          <div className="flex gap-6 mb-8 border-b border-[#2A1F14]/10">
            {["login", "register"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`tab-${m}`}
                className={`pb-3 text-sm tracking-wide transition-colors relative ${mode === m ? "text-gold" : "text-[#2A1F14]/40 hover:text-[#2A1F14]"}`}>
                {m === "login" ? "Accedi" : "Registrati"}
                {mode === m && <motion.div layoutId="tab" className="absolute bottom-0 left-0 right-0 h-px bg-gold" />}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
            <AnimatePresence mode="wait">
              {mode === "register" && (
                <motion.input key="name" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  required placeholder="Nome e cognome" value={form.name} onChange={set("name")} className={field} data-testid="auth-name" />
              )}
            </AnimatePresence>
            <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="auth-email" />
            <input required type="password" placeholder="Password" value={form.password} onChange={set("password")} className={field} data-testid="auth-password" />
            <button disabled={loading} data-testid="auth-submit" className="w-full py-4 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors disabled:opacity-50">
              {loading ? "Attendere..." : mode === "login" ? "Accedi" : "Crea il tuo account"}
            </button>
          </form>

          {mode === "login" && (
            <div className="mt-6 text-center">
              {!forgotOpen ? (
                <button onClick={() => setForgotOpen(true)} data-testid="forgot-open" className="text-[#2A1F14]/60 text-sm hover:text-gold link-underline">Password dimenticata?</button>
              ) : (
                <form onSubmit={sendForgot} className="text-left space-y-3 mt-4" data-testid="forgot-form">
                  <p className="text-[#2A1F14]/70 text-sm">Inserisci la tua email: se registrata, ti invieremo un link per reimpostare la password.</p>
                  <input required type="email" placeholder="Email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className={field} data-testid="forgot-email" />
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setForgotOpen(false)} className="flex-1 py-2.5 rounded-full border border-[#2A1F14]/20 text-[#2A1F14]/70 text-sm hover:border-gold hover:text-gold transition-colors">Annulla</button>
                    <button disabled={forgotLoading} type="submit" data-testid="forgot-submit" className="flex-1 py-2.5 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors disabled:opacity-50">{forgotLoading ? "Invio..." : "Invia link"}</button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
        <p className="text-center text-[#2A1F14]/30 text-xs mt-8">Area riservata agli studenti della LUX ACADEMY.</p>
      </div>
    </div>
  );
}
